import { useState, useEffect, useCallback, useRef } from 'react';

let sharedDragPayload = null;

export function useLayoutDrag() {
    const [leftWidth, setLeftWidth] = useState(() => {
        const v = Number(sessionStorage.getItem('console_left_w'));
        return Number.isFinite(v) && v > 0 ? v : 320;
    });
    const [rightWidth, setRightWidth] = useState(() => {
        const v = Number(sessionStorage.getItem('console_right_w'));
        return Number.isFinite(v) && v > 0 ? v : 360;
    });
    const [dragging, setDragging] = useState(null);
    const [leftSplitterFocused, setLeftSplitterFocused] = useState(false);
    const [rightSplitterFocused, setRightSplitterFocused] = useState(false);

    useEffect(() => { sessionStorage.setItem('console_left_w', String(leftWidth)); }, [leftWidth]);
    useEffect(() => { sessionStorage.setItem('console_right_w', String(rightWidth)); }, [rightWidth]);

    const onMouseMove = useCallback((e) => {
        if (!dragging) return;
        const containerWidth = window.innerWidth;
        if (dragging === 'left') {
            setLeftWidth(Math.min(Math.max(e.clientX - 12, 200), 600));
        } else if (dragging === 'right') {
            setRightWidth(Math.min(Math.max(containerWidth - e.clientX - 12, 280), 640));
        }
    }, [dragging]);

    const onMouseUp = useCallback(() => setDragging(null), []);

    useEffect(() => {
        if (dragging) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [dragging, onMouseMove, onMouseUp]);

    return {
        leftWidth, setLeftWidth,
        rightWidth, setRightWidth,
        dragging, setDragging,
        leftSplitterFocused, setLeftSplitterFocused,
        rightSplitterFocused, setRightSplitterFocused,
    };
}

function usePanelDrag() {
    const [isDragging, setIsDragging] = useState(false);
    const [activeDropTarget, setActiveDropTarget] = useState(null);
    const dragPayloadRef = useRef(null);
    const enterCountRef = useRef(0);

    const dragHandlers = useCallback((payload) => ({
        draggable: true,
        onDragStart: (e) => {
            dragPayloadRef.current = payload;
            sharedDragPayload = payload;
            setIsDragging(true);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', JSON.stringify(payload));
            if (e.currentTarget) {
                e.currentTarget.style.opacity = '0.4';
            }
        },
        onDragEnd: (e) => {
            setIsDragging(false);
            setActiveDropTarget(null);
            dragPayloadRef.current = null;
            sharedDragPayload = null;
            enterCountRef.current = 0;
            if (e.currentTarget) {
                e.currentTarget.style.opacity = '1';
            }
        },
    }), []);

    const getDragPayload = useCallback((e) => {
        if (dragPayloadRef.current) return dragPayloadRef.current;
        if (sharedDragPayload) return sharedDragPayload;
        try {
            const raw = e.dataTransfer.getData('text/plain');
            return raw ? JSON.parse(raw) : null;
        } catch (_) {
            return null;
        }
    }, []);

    const dropHandlers = useCallback((targetId, accepts, onDrop) => ({
        onDragOver: (e) => {
            const payload = getDragPayload(e);
            if (payload && accepts(payload)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
            }
        },
        onDragEnter: (e) => {
            e.preventDefault();
            enterCountRef.current++;
            const payload = getDragPayload(e);
            if (payload && accepts(payload)) {
                setActiveDropTarget(targetId);
            }
        },
        onDragLeave: () => {
            enterCountRef.current--;
            if (enterCountRef.current <= 0) {
                enterCountRef.current = 0;
                setActiveDropTarget(current => current === targetId ? null : current);
            }
        },
        onDrop: (e) => {
            e.preventDefault();
            enterCountRef.current = 0;
            setActiveDropTarget(null);
            const payload = getDragPayload(e);
            if (payload && accepts(payload)) {
                onDrop(payload);
            }
            dragPayloadRef.current = null;
            sharedDragPayload = null;
            setIsDragging(false);
        },
    }), [getDragPayload]);

    return {
        dragHandlers,
        dropHandlers,
        isDragging,
        activeDropTarget,
    };
}

export default usePanelDrag;
